import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';

/**
 * Halaman yang dituju dari tautan di email verifikasi.
 *
 * Publik, tidak menuntut sesi. Orang membuka tautannya dari aplikasi email,
 * sering di peramban yang berbeda dari tempat ia mendaftar, dan memaksanya
 * masuk lebih dulu berarti memaksanya mencari kata sandi sebelum bisa
 * menyelesaikan sesuatu yang sudah ia mulai.
 */
export const VerifikasiEmailPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { status: statusSesi } = useAuthStore();
  const token = params.get('token');

  const [keadaan, setKeadaan] = useState(token ? 'memproses' : 'tanpa-token');
  const [pesan, setPesan] = useState('');
  const sudahJalan = useRef(false);

  useEffect(() => {
    if (!token || sudahJalan.current) return;
    // React memanggil efek dua kali di mode pengembangan. Tanpa penjaga ini,
    // permintaan kedua akan menemukan tokennya sudah terpakai dan menampilkan
    // kegagalan padahal verifikasinya baru saja berhasil.
    sudahJalan.current = true;

    api
      .post('/auth/verify-email/confirm', { token })
      .then(() => setKeadaan('berhasil'))
      .catch((err) => {
        setKeadaan('gagal');
        setPesan(
          err instanceof ApiError
            ? err.message
            : 'Tautannya tidak bisa diproses. Coba minta yang baru dari Pengaturan.',
        );
      });
  }, [token]);

  const tujuan = statusSesi === 'authenticated' ? '/dashboard' : '/masuk';

  return (
    <div className="grid min-h-screen place-items-center bg-[var(--surface-canvas)] px-4">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="mb-8 inline-flex">
          <Logo className="h-8 w-auto" />
        </Link>

        <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-8 shadow-sm">
          {keadaan === 'memproses' && (
            <>
              <Loader2
                className="mx-auto size-8 animate-spin text-[var(--text-muted)]"
                aria-hidden="true"
              />
              <p className="mt-4 text-sm text-[var(--text-secondary)]">
                Sedang mengonfirmasi alamat email Anda...
              </p>
            </>
          )}

          {keadaan === 'berhasil' && (
            <>
              <div className="mx-auto grid size-14 place-items-center rounded-full bg-success-50 dark:bg-success-500/10">
                <CheckCircle2 className="size-7 text-success-600" aria-hidden="true" />
              </div>
              <h1 className="mt-4 text-xl font-bold text-[var(--text-primary)]">
                Email Anda terkonfirmasi
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
                Akun Anda kini aktif sepenuhnya. Selamat datang di Crawel.
              </p>
              <Button className="mt-6 w-full" onClick={() => navigate(tujuan, { replace: true })}>
                {statusSesi === 'authenticated' ? 'Ke Dashboard' : 'Masuk ke akun'}
              </Button>
            </>
          )}

          {(keadaan === 'gagal' || keadaan === 'tanpa-token') && (
            <>
              <div className="mx-auto grid size-14 place-items-center rounded-full bg-danger-50 dark:bg-danger-500/10">
                <AlertCircle className="size-7 text-danger-600" aria-hidden="true" />
              </div>
              <h1 className="mt-4 text-xl font-bold text-[var(--text-primary)]">
                Tautannya tidak bisa dipakai
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
                {keadaan === 'tanpa-token'
                  ? 'Alamat ini dibuka tanpa kode konfirmasi. Pastikan Anda menekan tautannya langsung dari email.'
                  : pesan}
              </p>
              <p className="mt-3 text-sm text-[var(--text-muted)]">
                Tautan konfirmasi berlaku 24 jam. Kalau sudah lewat, mintalah yang baru dari
                Pengaturan setelah masuk.
              </p>
              <Button
                variant="secondary"
                className="mt-6 w-full"
                onClick={() => navigate(tujuan, { replace: true })}
              >
                {statusSesi === 'authenticated' ? 'Ke Dashboard' : 'Masuk ke akun'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
